import { useWorkflowProgress } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/WorkflowProgress/WorkflowProgressContext';
import { SendDocumentModalContent } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/SendDocumentModalComponents';
import { DocumentSettings } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Modals/SendDocumentModal/types';
import { getDocumentsFilesData } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service';
import { simpleDocumentsWorkflowCreateDocuments } from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import { useDocumentCardsController } from '@dealstack/frontend/shared/components/DocumentCard/useDocumentCardsController';
import { ConfirmationModalV2 } from '@dealstack/frontend/shared/components/ConfirmationModal/ConfirmationModalV2';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { em } from '@mantine/core';
import { SortingOrder } from '@dealstack/shared/lib/frontend/common-ui/types';
import { CreateDocumentWorkflowDto, InvestorBulkActionDto } from '@dealstack/shared/lib/generated';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { pick } from 'lodash';
import { useState } from 'react';
import ReactModal from 'react-modal';
import styled from 'styled-components';

interface Props {
	className?: string;
	onClose: () => void;
	isLoading: boolean;
	selectedInvestorsIds: string[];
	bulkActionPayload?: InvestorBulkActionDto;
	totalRecipients?: number;
}

const SendDocumentModal = ({
	className,
	selectedInvestorsIds,
	bulkActionPayload,
	totalRecipients,
	isLoading,
	onClose,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const { startEmailWorkflow } = useWorkflowProgress();
	const tPrefix = 'investors.simple_document_workflow.template_section.confirmation_modal';
	const [isSuccess, setIsSuccess] = useState<boolean>(false);
	const [emailTextValue, setEmailTextValue] = useState<string>('');
	const [documentsDataArray, setDocumentsDataArray] = useState<DocumentSettings[]>([]);
	const [documentsFilesSearch, setDocumentsFilesSearch] = useState<string | undefined>();
	const [documentsFilesSorting, setDocumentsFilesSorting] = useState<SortingOrder>(
		SortingOrder.DESC
	);
	const [showSelectedDocuments, setShowSelectedDocuments] = useState<boolean>(false);

	const createDocumentRequest = useMutation({
		mutationFn: (variables: Parameters<typeof simpleDocumentsWorkflowCreateDocuments>[0]) =>
			simpleDocumentsWorkflowCreateDocuments(variables),

		onSuccess: (data) => {
			setIsSuccess(true);
			startEmailWorkflow(data.payload?.investorInviteWorkflowHandle);
		},
	});

	const getDocumentsFilesQuery = useInfiniteQuery({
		queryKey: [
			API_PATHS.MIP_SIMPLE_DOCUMENT_WORKFLOW_DOCUMENT_FILES_GET,
			documentsFilesSearch,
			documentsFilesSorting,
		],

		initialPageParam: '',
		queryFn: async ({ pageParam }) =>
			await getDocumentsFilesData(API_PATHS.MIP_SIMPLE_DOCUMENT_WORKFLOW_DOCUMENT_FILES_GET, {
				search: documentsFilesSearch,
				cursor: pageParam,
				sortingOrder: documentsFilesSorting,
			}),

		getNextPageParam: (lastPage) => lastPage.payload?.nextCursor,

		select: (data) => {
			if (!data.pages.length) {
				return { pages: [], pageParams: [] };
			}

			const transformedPages = data.pages.flatMap((page) => {
				if (!page?.payload?.documentFiles) {
					return [];
				}

				return page.payload.documentFiles;
			});

			return {
				pages: [transformedPages],
				pageParams: data.pageParams,
			};
		},
	});

	const {
		toggleCheckboxState,
		activeCheckboxesIds,
		onClickDropdown,
		openedDropdownDocumentCardId,
		onCloseDropdown,
	} = useDocumentCardsController({
		ids: getDocumentsFilesQuery.data?.pages[0].map((document) => document.id) ?? [],
	});

	const selectedDocumentsFiles = getDocumentsFilesQuery.data?.pages[0]?.filter((document) =>
		activeCheckboxesIds.includes(document.id)
	);

	const documentFiles = showSelectedDocuments
		? selectedDocumentsFiles
		: getDocumentsFilesQuery.data?.pages[0];

	const selectedDocumentsReadyToSend = documentsDataArray.filter(
		(document) =>
			activeCheckboxesIds.find((id) => id === document.documentWorkflowFileId) &&
			document.templateType !== undefined
	);

	const createDocuments = () => {
		const documents = selectedDocumentsReadyToSend.map((item) => {
			const parsedDocuments: CreateDocumentWorkflowDto = {
				documentWorkflowFileId: item.documentWorkflowFileId,
				templateType: item.templateType,
				requiredWitnesses: item.witnessesRequired ? 1 : 0,
				signatureType: item.signatureType,
				isSignatureAdvanced: item.isSignatureAdvanced,
				individualScheduleRequired: item.individualScheduleRequired,
				isToBeSubmitted: item.isToBeSubmitted,
				emailText: emailTextValue ?? null,
				guidanceNoteFileId: item.guidanceNote?.id ?? null,
				counterparties: item.counterpartiesIncluded
					? item.counterparties.map((cp) => pick(cp, ['name', 'amount']))
					: [],
				useManualSignaturePlacement: item.useManualSignaturePlacement ?? false,
				showStampMetadata: item.showStampMetadata ?? true,
				signaturePlacements: item.useManualSignaturePlacement
					? item.signaturePlacements ?? []
					: [],
			};
			return parsedDocuments;
		});
		createDocumentRequest.mutate({
			documents,
			...(bulkActionPayload ?? { selectedIds: selectedInvestorsIds }),
		});
		onClose();
	};

	const numberOfInvestors = totalRecipients ?? selectedInvestorsIds.length;

	const createdDocuments = selectedDocumentsReadyToSend.length;

	const titleTranslationKey =
		createdDocuments > 1 ? `${tPrefix}.title_many_doc` : `${tPrefix}.title_one_doc`;

	const descriptionTranslationKey =
		createdDocuments > 1
			? `${tPrefix}.description_many_investor`
			: `${tPrefix}.description_one_investor`;

	const confirmationTitleText = tMipManage(titleTranslationKey, {
		createdDocumentsNumber: createdDocuments.toString(),
	});

	const confirmationDescriptionText = tMipManage(descriptionTranslationKey);

	return (
		<ReactModal contentLabel="send-document-modal" isOpen className={className}>
			{isLoading ? <InlineLoader /> : null}
			{isSuccess ? (
				<ConfirmationModalV2
					onCancel={() => setIsSuccess(false)}
					onSubmit={createDocuments}
					title={confirmationTitleText}
					description={confirmationDescriptionText}
					disabledSubmitButton={createDocumentRequest.isPending}
				/>
			) : (
				<SendDocumentModalContent
					numberOfInvestors={numberOfInvestors}
					documentsDataArray={documentsDataArray}
					setDocumentsDataArray={setDocumentsDataArray}
					onClose={onClose}
					onClickSendDocuments={() => setIsSuccess(true)}
					selectedInvestorsIds={selectedInvestorsIds}
					emailTextValue={emailTextValue}
					setEmailTextValue={setEmailTextValue}
					selectedDocumentsReadyToSend={selectedDocumentsReadyToSend}
					documentFiles={documentFiles ?? []}
					activeCheckboxesIds={activeCheckboxesIds}
					documentsFilesSearch={documentsFilesSearch}
					documentsFilesSorting={documentsFilesSorting}
					setDocumentsFilesSearch={setDocumentsFilesSearch}
					setDocumentsFilesSorting={setDocumentsFilesSorting}
					setShowSelectedDocuments={setShowSelectedDocuments}
					showSelectedDocuments={showSelectedDocuments}
					onClickDropdown={onClickDropdown}
					openedDropdownDocumentCardId={openedDropdownDocumentCardId}
					onCloseDropdown={onCloseDropdown}
					toggleCheckboxState={toggleCheckboxState}
				/>
			)}
		</ReactModal>
	);
};

const Styled = styled(SendDocumentModal)`
	display: flex;
	flex-direction: column;
	width: 50rem;

	background-color: ${defaultTheme.v2.color.white[500]};
	border-radius: ${em(8)};
`;

export { Styled as SendDocumentModal };
